import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  PlayerModel,
  CreatePlayerRequest,
  UpdatePlayerRequest,
  UpdatePlayerActiveRequest,
  ListPlayersQuery,
  ListPlayersResponse,
  BulkUpdatePlayersRequest,
  BulkUpdatePlayersResponse,
} from './player.type';

@Injectable({
  providedIn: 'root',
})
export class PlayerService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiBaseURL}/players`;

  /**
   * Get list of players with optional filters and pagination
   */
  getPlayers(query?: ListPlayersQuery): Observable<ListPlayersResponse> {
    let params = new HttpParams();

    if (query) {
      if (query.quality !== undefined && query.quality !== null) {
        params = params.set('quality', query.quality.toString());
      }
      if (query.rarity_id !== undefined && query.rarity_id !== null) {
        params = params.set('rarity_id', query.rarity_id.toString());
      }
      if (query.active !== undefined && query.active !== null) {
        params = params.set('active', query.active.toString());
      }
      if (query.offset !== undefined && query.offset !== null) {
        params = params.set('offset', query.offset.toString());
      }
      if (query.limit !== undefined && query.limit !== null) {
        params = params.set('limit', query.limit.toString());
      }
    }

    return this.http.get<ListPlayersResponse>(this.apiUrl, { params });
  }

  /**
   * Get a single player by ID
   */
  getPlayer(id: number): Observable<PlayerModel> {
    return this.http.get<PlayerModel>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create a new player
   */
  createPlayer(request: CreatePlayerRequest): Observable<PlayerModel> {
    return this.http.post<PlayerModel>(this.apiUrl, request);
  }

  /**
   * Update a player (partial updates supported)
   */
  updatePlayer(id: number, request: UpdatePlayerRequest): Observable<PlayerModel> {
    return this.http.put<PlayerModel>(`${this.apiUrl}/${id}`, request);
  }

  /**
   * Quick toggle active status
   */
  updatePlayerActive(
    id: number,
    active: boolean
  ): Observable<PlayerModel> {
    const request: UpdatePlayerActiveRequest = { active };
    return this.http.put<PlayerModel>(`${this.apiUrl}/${id}/active`, request);
  }

  /**
   * Delete a player
   */
  deletePlayer(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Bulk update players
   */
  bulkUpdatePlayers(request: BulkUpdatePlayersRequest): Observable<BulkUpdatePlayersResponse> {
    return this.http.put<BulkUpdatePlayersResponse>(`${this.apiUrl}/bulk-update`, request);
  }

  /**
   * Triggers Player Update Job
   */
  autoUpdate(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/auto-update`, {});
  }
}
